import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Container,
    CircularProgress,
    Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getLatestRates, getCountryFromCoordinates } from '../services/exchangeRateService';
import { TOP_CURRENCIES, CURRENCY_TO_COUNTRY, COUNTRY_TO_CURRENCY } from '../constants';
import CurrencyExchangeForm from '../components/currency/CurrencyExchangeForm';
import CurrentRatesTable from '../components/currency/CurrentRatesTable';
import ReverseRatesTable from '../components/currency/ReverseRatesTable';
import CurrencySearch from '../components/currency/CurrencySearch';
import { useAuth } from '../contexts/AuthContext';

const UserEnhancedDashboard: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();
    
    // Use user preferences for default values, fallback to defaults
    const userPreferences = user?.preferences;
    const defaultFromCurrency = userPreferences?.defaultFromCurrency || 'USD';
    const defaultToCurrency = userPreferences?.defaultToCurrency || 'USD';
    const decimalPlaces = userPreferences?.calculatorSettings?.decimalPlaces || 2;
    const favoriteCurrencies = userPreferences?.favoriteCurrencies || [];
    const autoRefresh = userPreferences?.calculatorSettings?.autoRefresh || false;
    const showReverseRates = true; // Always show reverse rates for now
    
    console.log('👤 UserEnhancedDashboard: User preferences:', userPreferences);
    console.log('👤 UserEnhancedDashboard: Default currencies:', { defaultFromCurrency, defaultToCurrency });
    console.log('👤 UserEnhancedDashboard: Decimal places:', decimalPlaces);
    console.log('👤 UserEnhancedDashboard: Favorite currencies:', favoriteCurrencies);
    console.log('👤 UserEnhancedDashboard: Auto refresh:', autoRefresh);
    console.log('👤 UserEnhancedDashboard: Show reverse rates:', showReverseRates);

    const [amount, setAmount] = useState<string>('1');
    const [fromCurrency, setFromCurrency] = useState<string>(defaultFromCurrency);
    const [toCurrency, setToCurrency] = useState<string>(defaultToCurrency);

    // Debug fromCurrency changes
    useEffect(() => {
        console.log('👤 UserEnhancedDashboard: fromCurrency state changed to:', fromCurrency);
    }, [fromCurrency]);
    const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [exchangeRates, setExchangeRates] = useState<{ [key: string]: number }>({});
    const [customRates, setCustomRates] = useState<{ [key: string]: number }>({});
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [availableCurrencies] = useState<string[]>(Object.keys(CURRENCY_TO_COUNTRY));
    
    // Initialize selected currencies with user's favorites, fallback to TOP_CURRENCIES if no favorites
    const initialSelectedCurrencies = favoriteCurrencies.length > 0 
        ? favoriteCurrencies.slice(0, 10) // Use only user's favorites, limit to 10
        : TOP_CURRENCIES; // Fallback to default if no favorites
    
    const [selectedCurrencies, setSelectedCurrencies] = useState<string[]>(initialSelectedCurrencies);
    const [searchQuery, setSearchQuery] = useState('');
    const [locationDetected, setLocationDetected] = useState(false);

    // Apply language preference
    useEffect(() => {
        if (userPreferences?.language && i18n.language !== userPreferences.language) {
            console.log('👤 UserEnhancedDashboard: Changing language to:', userPreferences.language);
            i18n.changeLanguage(userPreferences.language);
        }
    }, [userPreferences?.language, i18n]);

    // Apply theme preference
    useEffect(() => {
        if (userPreferences?.theme) {
            console.log('👤 UserEnhancedDashboard: Applying theme:', userPreferences.theme);
            // Update theme in localStorage
            localStorage.setItem('theme', userPreferences.theme);
            // Trigger theme change in parent component
            window.dispatchEvent(new CustomEvent('themeChange', { detail: userPreferences.theme }));
        }
    }, [userPreferences?.theme]);

    // Update currencies when user preferences change
    useEffect(() => {
        if (userPreferences) {
            console.log('👤 UserEnhancedDashboard: Updating currencies from preferences');
            console.log('👤 UserEnhancedDashboard: New fromCurrency:', userPreferences.defaultFromCurrency);
            console.log('👤 UserEnhancedDashboard: New toCurrency:', userPreferences.defaultToCurrency);
            setFromCurrency(userPreferences.defaultFromCurrency || 'USD');
            setToCurrency(userPreferences.defaultToCurrency || 'USD');
            
            // Update selected currencies with user's favorites only
            const newSelectedCurrencies = userPreferences.favoriteCurrencies.length > 0
                ? userPreferences.favoriteCurrencies.slice(0, 10) // Use only favorites
                : TOP_CURRENCIES; // Fallback to default if no favorites
            setSelectedCurrencies(newSelectedCurrencies);
        }
    }, [userPreferences]);

    // Detect user location and set appropriate currency (only if no user preference)
    const detectUserLocation = useCallback(async () => {
        // Skip location detection if user has preferences set
        if (userPreferences?.defaultToCurrency) {
            console.log('👤 UserEnhancedDashboard: Skipping location detection - user has preferences');
            return;
        }

        try {
            if (!navigator.geolocation) {
                console.log('Geolocation not supported, using default currency');
                return;
            }

            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                    enableHighAccuracy: true,
                    timeout: 10000,
                    maximumAge: 600000, // 10 minutes
                });
            });

            const { latitude, longitude } = position.coords;
            const countryCode = await getCountryFromCoordinates(latitude, longitude);
            const locationCurrency = COUNTRY_TO_CURRENCY[countryCode];

            if (locationCurrency && availableCurrencies.includes(locationCurrency)) {
                setToCurrency(locationCurrency);
                setLocationDetected(true);
                console.log(`Location detected: ${countryCode}, currency set to: ${locationCurrency}`);
            } else {
                console.log(`No currency mapping found for country: ${countryCode}, using default`);
            }
        } catch (error) {
            console.log('Location detection failed, using default currency:', error);
        }
    }, [availableCurrencies, userPreferences?.defaultToCurrency]);

    // Fetch exchange rates
    const fetchExchangeRates = useCallback(async (baseCurrency: string) => {
        setIsRefreshing(true);
        try {
            const response = await getLatestRates(baseCurrency);
            setExchangeRates(response.conversion_rates);
            setLastUpdated(new Date());
            setError(null);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to fetch exchange rates';
            setError(errorMessage);
        } finally {
            setIsRefreshing(false);
        }
    }, []);

    // Initial load with location detection
    useEffect(() => {
        const initializeApp = async () => {
            try {
                // First detect location (only if no user preferences)
                await detectUserLocation();

                // Then fetch exchange rates
                await fetchExchangeRates(defaultFromCurrency);
                setLoading(false);
            } catch (error) {
                setError('Failed to initialize exchange rates');
                setLoading(false);
            }
        };

        initializeApp();
    }, [detectUserLocation, fetchExchangeRates, defaultFromCurrency]);

    // Handle FROM currency change
    const handleFromCurrencyChange = useCallback((newCurrency: string) => {
        // Reset amount to 1 when FROM currency changes
        setAmount('1');
        setFromCurrency(newCurrency);
        setConvertedAmount(null);
        fetchExchangeRates(newCurrency);
    }, [fetchExchangeRates]);

    // Handle TO currency change
    const handleToCurrencyChange = useCallback((newCurrency: string) => {
        // Reset amount to 1 when TO currency changes
        setAmount('1');
        setToCurrency(newCurrency);
        setConvertedAmount(null);
    }, []);

    // Handle manual refresh
    const handleManualRefresh = () => {
        fetchExchangeRates(fromCurrency);
    };

    // Handle rate override
    const handleRateOverride = useCallback((rate: number) => {
        setCustomRates(prev => ({
            ...prev,
            [toCurrency]: rate
        }));
    }, [toCurrency]);

    // Get effective rate (custom or API)
    const getEffectiveRates = useCallback(() => {
        return {
            ...exchangeRates,
            ...customRates
        };
    }, [exchangeRates, customRates]);

    // Calculate conversion with user's decimal places preference
    const handleCalculate = useCallback(() => {
        if (!amount || isNaN(Number(amount))) {
            setConvertedAmount(null);
            return;
        }

        try {
            const effectiveRates = getEffectiveRates();
            const rate = effectiveRates[toCurrency];
            if (!rate) {
                setError('Exchange rate not available');
                return;
            }

            const converted = Number(amount) * rate;
            let formattedAmount: number;

            if (toCurrency === 'IRR') {
                // IRR amounts are large whole numbers
                formattedAmount = Math.round(converted);
            } else {
                // Use user's preferred decimal places
                formattedAmount = Number(converted.toFixed(decimalPlaces));
            }

            setConvertedAmount(formattedAmount);
            setError(null);
        } catch (err) {
            setError('Error calculating conversion');
            console.error('Calculation error:', err);
        }
    }, [amount, toCurrency, getEffectiveRates, decimalPlaces]);

    // Auto-calculate when amount changes
    useEffect(() => {
        handleCalculate();
    }, [amount, handleCalculate]);

    // Auto-refresh functionality (simplified - refresh every 5 minutes if enabled)
    useEffect(() => {
        if (!autoRefresh) return;

        console.log('👤 UserEnhancedDashboard: Setting up auto-refresh every 5 minutes');
        const interval = setInterval(() => {
            console.log('👤 UserEnhancedDashboard: Auto-refreshing exchange rates');
            fetchExchangeRates(fromCurrency);
        }, 5 * 60 * 1000); // 5 minutes

        return () => {
            console.log('👤 UserEnhancedDashboard: Clearing auto-refresh interval');
            clearInterval(interval);
        };
    }, [autoRefresh, fromCurrency, fetchExchangeRates]);

    // Handle swap currencies
    const handleSwapCurrencies = useCallback(() => {
        // Swap currencies
        const newFromCurrency = toCurrency;
        const newToCurrency = fromCurrency;

        // Swap amounts: the converted amount becomes the new input amount
        // Handle very small numbers (like IRR conversions) with proper precision
        let newAmount = amount;
        if (convertedAmount !== null) {
            if (toCurrency === 'IRR') {
                // IRR amounts are large whole numbers
                newAmount = Math.round(convertedAmount).toString();
            } else if (fromCurrency === 'IRR') {
                // Converting FROM IRR results in very small numbers
                // Use toFixed with enough precision to avoid scientific notation
                newAmount = convertedAmount.toFixed(10).replace(/\.?0+$/, '');
            } else {
                // Regular conversions - use user's decimal places preference
                newAmount = convertedAmount.toFixed(decimalPlaces).replace(/\.?0+$/, '');
            }
        }

        setFromCurrency(newFromCurrency);
        setToCurrency(newToCurrency);
        setAmount(newAmount);
        setConvertedAmount(null);

        // CRITICAL: Fetch new exchange rates for the new base currency
        // Without this, we're using old rates and calculations will be wrong!
        fetchExchangeRates(newFromCurrency);
    }, [fromCurrency, toCurrency, amount, convertedAmount, fetchExchangeRates, decimalPlaces]);

    // Add currency to selected list
    const handleAddCurrency = (currency: string) => {
        if (!selectedCurrencies.includes(currency)) {
            setSelectedCurrencies([...selectedCurrencies, currency]);
        }
        setSearchQuery('');
    };

    // Remove currency from selected list
    const handleRemoveCurrency = (currency: string) => {
        setSelectedCurrencies(selectedCurrencies.filter(c => c !== currency));
    };

    if (loading) {
        return (
            <Container maxWidth="lg">
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {locationDetected && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    {t('location.detected')}
                </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {/* Currency Exchange Form */}
                <CurrencyExchangeForm
                    amount={amount}
                    fromCurrency={fromCurrency}
                    toCurrency={toCurrency}
                    convertedAmount={convertedAmount}
                    exchangeRates={getEffectiveRates()}
                    lastUpdated={lastUpdated}
                    error={error}
                    availableCurrencies={availableCurrencies}
                    onAmountChange={setAmount}
                    onFromCurrencyChange={handleFromCurrencyChange}
                    onToCurrencyChange={handleToCurrencyChange}
                    onSwapCurrencies={handleSwapCurrencies}
                    onRefresh={handleManualRefresh}
                    isRefreshing={isRefreshing}
                    onRateOverride={handleRateOverride}
                />

                {/* Currency Search and Rates Tables */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: showReverseRates 
                        ? { xs: '1fr', lg: '1fr 1fr' } 
                        : { xs: '1fr', lg: '1fr' },
                    gap: 3
                }}>
                    {/* Left Column - Current Rates */}
                    <Box>
                        <CurrencySearch
                            searchQuery={searchQuery}
                            selectedCurrencies={selectedCurrencies}
                            availableCurrencies={availableCurrencies}
                            onSearchChange={setSearchQuery}
                            onAddCurrency={handleAddCurrency}
                        />
                        <CurrentRatesTable
                            fromCurrency={fromCurrency}
                            selectedCurrencies={selectedCurrencies}
                            exchangeRates={exchangeRates}
                            isRefreshing={isRefreshing}
                            onRefresh={handleManualRefresh}
                            onRemoveCurrency={handleRemoveCurrency}
                        />
                    </Box>

                    {/* Right Column - Reverse Rates (conditional) */}
                    {showReverseRates && (
                        <Box>
                            <ReverseRatesTable
                                fromCurrency={fromCurrency}
                                selectedCurrencies={selectedCurrencies}
                                exchangeRates={exchangeRates}
                                isRefreshing={isRefreshing}
                                onRefresh={handleManualRefresh}
                            />
                        </Box>
                    )}
                </Box>

            </Box>
        </Container>
    );
};

export default UserEnhancedDashboard;
